import React, { useState } from "react";
import { useDispatch } from "react-redux";
import ClockCircleOutlined from "@ant-design/icons/ClockCircleOutlined";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Button, Form, Input, Tooltip } from "antd";
import styles from "./Login.module.scss";

import logo from "../../Image/logo.png";
import DownloadIcons from "./DownloadIcons";
import {
  generateLines,
  generatePoints,
  isMobileDevice,
} from "../../Utils/Utils";
import LoginFunction from "../../Functions/Login/Login";
import { setAccess } from "../../Redux/Access/accessSlice";
import { setUser } from "../../Redux/User/userSlice";
import UserManagementFunction from "../../Functions/UserManagement/UserManagement";
import { setCustomer } from "../../Redux/Customer/customerSlice";
import { setIdleTimeInSeconds } from "../../Redux/AutoLogout/autoLogoutSlice";
import { Point } from "../../Utils/types";
import { setVersion } from "../../Redux/App/AppSlice";
import metadata from "../../metadata.json";

const Login: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showError, setShowError] = useState<boolean>(false);
  const revision = metadata;

  const numPoints = isMobileDevice() ? 15 : 30; // Random number of points between 30 and 50
  const initialSpeed = 0.2; // Adjust this value to control the initial movement speed
  const circlePoints: Point[] = generatePoints({ numPoints, initialSpeed });
  const lines = generateLines(circlePoints, "rgba(255, 255, 255, 0.10)");

  const login = async (values: { username: string; password: string }) => {
    try {
      setShowError(false);
      setErrorMessage("");
      setLoading(true);

      const tokenData = await LoginFunction.generateAccessToken();

      if (!tokenData.status) {
        throw new Error("Error Generating Access Token");
      }
      const userData = await LoginFunction.login(
        tokenData.data.access_token,
        values.username,
        values.password
      );

      if (!userData.status) {
        throw new Error("Error Generating Session Token");
      }

      if (userData.data.user.loginType === "admin") {
        UserManagementFunction.getCustomerFeatures(
          tokenData.data.access_token,
          userData.data.token,
          {
            custId: userData.data.user.custID.toString(),
          }
        )
          .then((response: any) => {
            if (response.data.success) {
              dispatch(setCustomer(response.data.customerFeatures));
            } else {
              setLoading(false);
              setShowError(true);
              setErrorMessage(response.message);
            }
          })
          .catch((error: any) => {
            setLoading(false);
            setShowError(true);
            setErrorMessage("Something went wrong");
          });
      }

      document.title = "Wiline Admin Portal";

      dispatch(setUser(userData.data));
      dispatch(setAccess(tokenData.data));
      dispatch(
        setIdleTimeInSeconds({
          idleTimeInSeconds: userData.data.inactiveSessionTimeout,
        })
      );
      dispatch(setVersion(revision));

      navigate("/user-management");
    } catch (error: Error | any) {
      setShowError(true);
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-loader">
      <div className="background"></div>
      <svg className="web" xmlns="http://www.w3.org/2000/svg">
        <g className="lines">{lines}</g>
        {circlePoints.map((point, index) => (
          <circle
            key={index}
            cx={`${point.x}%`}
            cy={`${point.y}%`}
            r={3}
            fill="rgba(255, 255, 255, 0.45)"
            className="moving-point"
          />
        ))}
      </svg>
      <div className="center">
        <img
          src={logo}
          alt="WiLine logo"
          style={{ height: "100px", marginBottom: "20px" }}
        />
        <div className={`${styles.formContainer}`}>
          <Form
            name="normal_login"
            className={styles.loginForm}
            initialValues={{ remember: true }}
            onFinish={login}
          >
            <Form.Item
              name="username"
              rules={[
                { required: true, message: "Please input your Username!" },
              ]}
            >
              <Input
                prefix={<UserOutlined className="site-form-item-icon" />}
                placeholder="Username"
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[
                { required: true, message: "Please input your Password!" },
              ]}
            >
              <Input
                prefix={<LockOutlined className="site-form-item-icon" />}
                type="password"
                placeholder="Password"
              />
            </Form.Item>
            {showError && (
              <Form.Item className={styles.inputContainer}>
                <Input
                  className={styles.disabledInput}
                  status="warning"
                  placeholder="Warning"
                  defaultValue={errorMessage}
                  prefix={<ClockCircleOutlined />}
                />
              </Form.Item>
            )}
            <Form.Item>
              <Button
                loading={loading}
                type="primary"
                htmlType="submit"
                className={styles.loginFormButton}
              >
                LOGIN
              </Button>
            </Form.Item>
          </Form>
          <Tooltip
            placement="left"
            title="If you don't have a login, please contact your local WiLine UCass Admin."
          >
            <div className={styles["need-account"]}>
              <p>Need an account?</p>
            </div>
          </Tooltip>

          <DownloadIcons />
        </div>
      </div>
    </div>
  );
};

export default Login;
